import { useState, type SubmitEvent } from "react";
import { Element } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import { submitContact } from "../../lib/runtimePage";
import {
  type BorderSide,
  type RuntimeProps,
  type SubmitState,
  SECTION_REVEAL_PROPS,
  StatusMessage,
  SubmitStateContext,
  ThemeSectionContainer,
  WB_VALIDATE_EVENT,
  classNames,
  collectFieldValues,
  isVisible,
  parseContentAlignmentClass,
  parseUnknownBoolean,
} from "./shared";
import { TextChild } from "./textChild";
import { Button } from "./button";
import { ContactSocial } from "./appBar";
import { Checkboxes, FieldsGroup } from "./formGroups";
import { useEventTimezone } from "../theme/WBThemeRuntimeProvider";
import { submitErrorText } from "../lib/submit-error";

export function Contact(props: RuntimeProps) {
  const [state, setState] = useState<SubmitState>({ loading: false });
  const eventTz = useEventTimezone();
  const infoSection = props.infoSection as boolean | undefined;
  const infoWidth = props.infoWidth as "100%" | "50%" | undefined;
  const inversed = Boolean(props.inversed);
  const showInfo = infoSection !== false;
  const useSplitLayout = infoWidth === "50%";
  const textSpacing = props.textSpacing as string | undefined;
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );

  if (!isVisible(props.isVisible as boolean | undefined)) return null;

  async function onSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;

    // Dispatch validation event — each FormInput / Checkbox increments errorCount
    const detail = { errorCount: 0 };
    form.dispatchEvent(new CustomEvent(WB_VALIDATE_EVENT, { detail }));
    if (detail.errorCount > 0) return;

    setState({ loading: true });
    const { values, checkboxes } = collectFieldValues(form, eventTz);
    const consent =
      parseUnknownBoolean(values.emailOptIn) ??
      parseUnknownBoolean(values.consent) ??
      checkboxes.some((item) => item.checked);

    // Build payload from all collected field values (supports custom fields)
    const fieldValues: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(values)) {
      fieldValues[key] = val == null ? "" : String(val);
    }
    fieldValues.consent = consent;

    try {
      const response = await submitContact({ values: fieldValues });
      setState({
        loading: false,
        success: String(response.message ?? "Contact message sent."),
      });
      form.reset();
    } catch (error) {
      // Audit 2026-09-23 (4f): status/errorCode ⇒ localized.
      setState({
        loading: false,
        error: submitErrorText(error, "Contact submit failed."),
      });
    }
  }

  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={props.padding as string | undefined}
      sectionMargin={props.sectionMargin as string | undefined}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
    >
      <motion.form
        noValidate
        onSubmit={onSubmit}
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          (props.isVisibleHeadline as boolean | undefined) !== false ||
            (props.isVisibleHeader as boolean | undefined) !== false ||
            (props.isVisibleText as boolean | undefined) !== false
            ? "gap-5"
            : undefined,
          alignmentClass,
          "items-center flex",
          useSplitLayout
            ? inversed
              ? "flex-col wb-md:flex-row-reverse max-w-275"
              : "flex-col wb-md:flex-row max-w-275"
            : inversed
              ? "flex-col-reverse max-w-275"
              : "flex-col max-w-275",
          "w-full",
        )}
      >
        {showInfo ? (
          <div
            className={classNames(
              useSplitLayout
                ? "w-full wb-md:w-1/2"
                : "w-full",
              textSpacing ?? "space-y-2 wb-md:space-y-3 wb-lg:space-y-4",
            )}
          >
            <Element is={TextChild} id="headline" />
            <Element is={TextChild} id="header" />
            <Element is={TextChild} id="text" />
            <div className="hidden wb-md:block">
              <Element is={ContactSocial} id="social" />
            </div>
          </div>
        ) : null}

        <div
          className={classNames(
            "flex flex-col justify-center",
            useSplitLayout
              ? "w-full wb-md:w-1/2"
              : "w-full",
          )}
        >
          <Element is={FieldsGroup} id="contactFields" canvas />
          <div className="mt-4">
            <Element is={Checkboxes} id="checkboxes" />
          </div>
          <div className="mt-6 w-full flex justify-end">
            {/* `loading` on <Element> is only a seed (audit 2026-09-23, 4b). */}
            <SubmitStateContext.Provider
              value={{ isSubmitted: Boolean(state.success), loading: state.loading }}
            >
              <Element
                is={Button}
                id="button"
                buttonType="submit"
                loading={state.loading}
              />
            </SubmitStateContext.Provider>
          </div>
        </div>

        <StatusMessage state={state} />
      </motion.form>
    </ThemeSectionContainer>
  );
}
